import styled from "styled-components";
import moment from "moment";

import { concatParams, CreateParamsJson } from "../../helper/pathHelper";
import {
  DndContext,
  closestCenter,
  PointerSensor,
  KeyboardSensor,
  useSensor,
  useSensors,
} from "@dnd-kit/core";
import {
  SortableContext,
  rectSortingStrategy,
  useSortable,
  sortableKeyboardCoordinates,
} from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";
import { useNavigate } from "react-router-dom";
import {
  formatTime,
  getEndtime,
  getStarttime,
  computeAgendaStatuses,
} from "../../helper/timeCalculations";
import { useEffect, useState } from "react";
import {
  Divider,
  IconButton,
  ListItemIcon,
  ListItemText,
  Menu,
  MenuItem,
  TextField,
} from "@mui/material";
import {
  Check,
  ContentCopy,
  Delete,
  MoreVert,
  TaskAlt,
  DragIndicator,
  CenterFocusStrong,
  LocalCafe,
  SelfImprovement,
  LunchDining,
  FlagCircle,
  EditNote,
} from "@mui/icons-material";
import { CARD_TYPES } from "../../helper/cardTypes";
import { getCardTypeIcon } from "./CardTypeIcon";
import CardDetailsDialog from "./CardDetailsDialog";
import { finishActive, planClock } from "../../helper/liveControls";
import { useLanguage } from "../../i18n/LanguageContext";

// Maps a card's topic text to a small icon, matched case-insensitively so it
// also picks up the Pomodoro example agendas ("Focus", "Short Break", "Long
// Break", "Lunch Break", "Wrap Up", in German "Fokus", "Kurze Pause", ...)
// and any card the user renames to match. More specific keywords are
// checked before the generic "break"/"pause" catch-all.
function getTopicIcon(topic) {
  const text = (topic || "").toLowerCase();
  if (text.includes("lunch") || text.includes("mittag")) return LunchDining;
  if (text.includes("long break") || text.includes("lange pause"))
    return SelfImprovement;
  if (text.includes("wrap up") || text.includes("abschluss")) return FlagCircle;
  if (text.includes("break") || text.includes("pause")) return LocalCafe;
  if (text.includes("focus") || text.includes("fokus")) return CenterFocusStrong;
  return null;
}

export const CARD_COLOR = {
  done: "#EFDFA9",
  active: "#EBC3B8",
  next: "#BFD9E2",
  upcoming: "#C7DFC7",
};

export const CARD_ROTATION = {
  done: "0deg",
  active: "0deg",
  next: "0.8deg",
  upcoming: "0deg",
};

// i18n keys of the status tags.
const TAG_TEXT = {
  done: "card.tagDone",
  active: "card.tagNow",
  next: "card.tagNext",
  upcoming: null,
};

const TAG_COLOR = {
  done: "#8A8368",
  active: "#9A4B4F",
  next: "#3E6E82",
  upcoming: null,
};

// Ticked-off cards get their own look instead of borrowing the faded
// "time is up" one: a clean paper card with a green check and outline.
const CHECKED_COLOR = "#F7F2E2";
const CHECKED_ACCENT = "#4F7A52";

const DragAndDropContainer = styled.div`
  display: block;
  width: 100%;
`;

const ListContainer = styled.div`
  margin: 8px 0;
  width: 100%;
  display: flex;
  flex-direction: column;
`;

// Fixed column counts (rather than size-based wrapping) so a minimum
// number of cards per row is guaranteed regardless of how much width the
// right column actually has: 2 on mobile, 3 on a tablet held in portrait,
// 4 from landscape-tablet/desktop widths up.
const TaskList = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 10px;
  flex-grow: 1;
  min-height: 100px;
  width: 100%;

  > * {
    flex: 0 0 calc((100% - 10px) / 2);
    max-width: calc((100% - 10px) / 2);
  }

  @media (min-width: 761px) {
    > * {
      flex-basis: calc((100% - 20px) / 3);
      max-width: calc((100% - 20px) / 3);
    }
  }

  @media (min-width: 1024px) {
    > * {
      flex-basis: calc((100% - 30px) / 4);
      max-width: calc((100% - 30px) / 4);
    }
  }
`;

// $-prefixed props are "transient": styled-components keeps them for
// styling but never forwards them to the underlying DOM node.
const Card = styled.div`
  position: relative;
  padding: 10px 11px 8px;
  border-radius: 3px;
  border-left: ${(props) =>
    props.$typeColor ? `5px solid ${props.$typeColor}` : "none"};
  background: ${(props) =>
    props.$checked ? CHECKED_COLOR : CARD_COLOR[props.$status]};
  opacity: ${(props) =>
    props.$status === "done" && !props.$checked ? 0.6 : 1};
  transform: rotate(
    ${(props) => (props.$isDragging ? "0deg" : CARD_ROTATION[props.$status])}
  );
  box-shadow: ${(props) =>
    props.$status === "active"
      ? "0 10px 18px -8px rgba(154, 75, 79, 0.5), 0 0 0 2px #9A4B4F"
      : props.$isDragging
      ? "0 12px 20px -10px rgba(46, 42, 34, 0.5)"
      : props.$checked
      ? `0 4px 10px -6px rgba(46, 42, 34, 0.35), inset 0 0 0 1.5px ${CHECKED_ACCENT}`
      : "0 4px 10px -6px rgba(46, 42, 34, 0.35)"};
  transition: box-shadow 0.15s ease, opacity 0.2s ease,
    background-color 0.25s ease;

  ${(props) =>
    props.$status === "active" &&
    `
    &::before {
      content: "";
      position: absolute;
      top: -6px;
      left: 14px;
      width: 10px;
      height: 10px;
      border-radius: 50%;
      background: #9A4B4F;
      box-shadow: 0 2px 3px rgba(0, 0, 0, 0.35);
    }
  `}
`;

const CardHeader = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 6px;
  margin-bottom: 2px;
`;

// A dedicated, always-visible grab target for starting a drag - without it,
// dragHandleProps sits on the whole card and the only way to pick it up is
// to find a spot the text field, time field and delete button don't cover.
const DragHandle = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
  width: 26px;
  height: 26px;
  margin: -6px 0 -6px -8px;
  border-radius: 4px;
  color: #8a8368;
  cursor: grab;
  touch-action: none;

  &:hover {
    background: rgba(46, 42, 34, 0.08);
    color: #2e2a22;
  }

  &:active {
    cursor: grabbing;
  }

  svg {
    font-size: 1.15rem;
  }
`;

const HeaderRight = styled.div`
  display: flex;
  align-items: center;
  gap: 6px;
  min-width: 0;
`;

// Round, hand-made looking tick target: a dashed ring that fills green with
// a little pop when the card is ticked off.
const CheckButton = styled.button`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
  width: 22px;
  height: 22px;
  margin: -4px 0;
  padding: 0;
  border-radius: 50%;
  border: 1.5px ${(props) => (props.$checked ? "solid" : "dashed")}
    ${(props) => (props.$checked ? CHECKED_ACCENT : "#8a8368")};
  background: ${(props) => (props.$checked ? CHECKED_ACCENT : "transparent")};
  color: ${(props) => (props.$checked ? "#fffbf2" : "transparent")};
  cursor: pointer;
  transition: background-color 0.2s ease, border-color 0.2s ease,
    color 0.2s ease, transform 0.15s ease;
  animation: ${(props) => (props.$checked ? "card-check-pop 0.3s ease" : "none")};

  svg {
    font-size: 0.95rem;
  }

  &:hover {
    border-color: ${CHECKED_ACCENT};
    color: ${(props) => (props.$checked ? "#fffbf2" : CHECKED_ACCENT)};
    transform: scale(1.08);
  }

  &:focus-visible {
    outline: 2px solid ${CHECKED_ACCENT};
    outline-offset: 2px;
  }
`;

const CheckedTag = styled.span`
  font-family: "Space Mono", monospace;
  font-size: 0.56rem;
  font-weight: 700;
  letter-spacing: 0.02em;
  padding: 1px 6px;
  border-radius: 10px;
  border: 1px solid ${CHECKED_ACCENT};
  color: ${CHECKED_ACCENT};
  white-space: nowrap;
  min-width: 0;
  overflow: hidden;
  text-overflow: ellipsis;
`;

const Tag = styled.span`
  font-family: "Space Mono", monospace;
  font-size: 0.56rem;
  font-weight: 700;
  letter-spacing: 0.02em;
  padding: 1px 6px;
  border-radius: 10px;
  background: ${(props) => TAG_COLOR[props.$status]};
  color: #fffbf2;
  white-space: nowrap;
  min-width: 0;
  overflow: hidden;
  text-overflow: ellipsis;
`;

const TopicRow = styled.div`
  display: flex;
  align-items: center;
  gap: 5px;
`;

const TopicIconWrap = styled.span`
  display: inline-flex;
  flex-shrink: 0;
  color: #8a8368;
`;

const TopicInput = styled.input`
  display: block;
  flex: 1;
  min-width: 0;
  border: none;
  border-bottom: 1px dashed transparent;
  background: transparent;
  font-family: "Kalam", cursive;
  font-weight: 700;
  font-size: 0.92rem;
  line-height: 1.2;
  margin: 0 0 3px;
  padding: 0 6px 1px 0;
  color: #2e2a22;
  text-decoration: ${(props) =>
    props.$status === "done" && !props.$checked ? "line-through" : "none"};

  &:focus {
    outline: none;
    border-bottom-color: #9a4b4f;
  }

  &::placeholder {
    color: #8a8368;
    font-weight: 400;
    font-style: italic;
  }
`;

const TimeRange = styled.div`
  font-family: "Space Mono", monospace;
  font-size: 0.66rem;
  color: #6c6552;
  margin-bottom: 6px;
`;

// Who runs the card and its note, shown under the time range. Clicking
// either opens the details dialog.
const CardDetails = styled.button`
  display: block;
  width: 100%;
  margin: -2px 0 6px;
  padding: 0;
  border: none;
  background: transparent;
  text-align: left;
  font-family: "Nunito Sans", sans-serif;
  font-size: 0.72rem;
  line-height: 1.35;
  color: #4a4436;
  cursor: pointer;

  .card-owner {
    font-weight: 700;
  }

  .card-note {
    display: -webkit-box;
    -webkit-line-clamp: 2;
    -webkit-box-orient: vertical;
    overflow: hidden;
    white-space: pre-line;
    font-style: italic;
  }

  &:focus-visible {
    outline: 2px solid #9a4b4f;
    outline-offset: 2px;
  }
`;

const CardFooter = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 2px;
`;

function moveArray(array, old_index, new_index) {
  const arr = JSON.parse(JSON.stringify(array));
  if (new_index >= arr.length) {
    var k = new_index - arr.length + 1;
    while (k--) {
      arr.push(undefined);
    }
  }
  arr.splice(new_index, 0, arr.splice(old_index, 1)[0]);
  return arr; // for testing
}

function draggableId(task) {
  return `draggable${task.id + 1}`;
}

// `active`/`over` carry ids of positions within the currently rendered
// (possibly filtered, when "hide past cards" is on) list, not the full
// agenda - `visible` maps each of those ids back to where the card
// actually lives in the full array, so the reorder still lands right.
function onDragEnd(event, agenda, visible, json, navigate, path) {
  const { active, over } = event;
  if (!over || active.id === over.id) {
    return;
  }

  const sourceIndex = visible.findIndex(
    (item) => draggableId(item.task) === active.id
  );
  const destinationIndex = visible.findIndex(
    (item) => draggableId(item.task) === over.id
  );
  if (sourceIndex === -1 || destinationIndex === -1) {
    return;
  }

  const originalSourceIndex = visible[sourceIndex].originalIndex;
  const originalDestinationIndex = visible[destinationIndex].originalIndex;
  const newArray = moveArray(agenda, originalSourceIndex, originalDestinationIndex);

  json.agenda = JSON.stringify(newArray);
  const params = concatParams({ ...json });
  navigate({
    pathname: path,
    search: params,
  });
}

function DroppableContainer({ visible, path }) {
  const items = visible.map((item) => draggableId(item.task));
  return (
    <ListContainer>
      <SortableContext items={items} strategy={rectSortingStrategy}>
        <TaskList>
          {visible.map((item) => (
            <DraggableContainer
              key={item.task.id}
              task={item.task}
              originalIndex={item.originalIndex}
              path={path}
              status={item.status}
            />
          ))}
        </TaskList>
      </SortableContext>
    </ListContainer>
  );
}

function DraggableContainer({ task, originalIndex, path, status }) {
  const json = CreateParamsJson();
  const navigate = useNavigate();

  const agenda = JSON.parse(json.agenda);
  const starttime = moment(json.starttime);

  const array = agenda.slice(0, originalIndex + 1);
  const deleteArray = agenda.filter((element) => element.id !== task.id);

  const taskStart = getStarttime(array, starttime);
  const taskEnd = getEndtime(array, starttime);
  const TopicIcon = getCardTypeIcon(task.type) || getTopicIcon(task.topic);
  const typeInfo = CARD_TYPES[task.type];
  const checked = Boolean(task.done);
  const [menuAnchor, setMenuAnchor] = useState(null);
  const [detailsOpen, setDetailsOpen] = useState(false);
  const { t } = useLanguage();

  function saveAgenda(newAgenda) {
    navigate({
      pathname: path,
      search: concatParams({
        ...json,
        agenda: JSON.stringify(newAgenda),
      }),
    });
  }

  function updateTask(changes) {
    agenda[originalIndex] = { ...agenda[originalIndex], ...changes };
    saveAgenda(agenda);
  }

  // Ticks the card off; if it's the one running right now, it also shrinks
  // its timebox to the time actually spent so the next card starts at once.
  function completeTask() {
    if (status === "active") {
      navigate({ pathname: path, search: concatParams(finishActive(json)) });
      return;
    }
    updateTask({ done: true });
  }

  function duplicateTask() {
    const copy = {
      ...task,
      id: moment().toISOString(),
      done: false,
    };
    agenda.splice(originalIndex + 1, 0, copy);
    saveAgenda(agenda);
  }

  const { attributes, listeners, setNodeRef, transform, transition, isDragging } =
    useSortable({ id: draggableId(task) });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    position: "relative",
    zIndex: isDragging ? 1 : "auto",
  };

  return (
    <Card
      ref={setNodeRef}
      style={style}
      $isDragging={isDragging}
      $status={status}
      $checked={checked}
      $typeColor={typeInfo?.color}
    >
      <CardHeader>
        <DragHandle
          {...attributes}
          {...listeners}
          aria-label={t("card.drag")}
        >
          <DragIndicator fontSize="inherit" />
        </DragHandle>
        <HeaderRight>
          {checked ? (
            <CheckedTag>{t("common.completed")}</CheckedTag>
          ) : (
            TAG_TEXT[status] && <Tag $status={status}>{t(TAG_TEXT[status])}</Tag>
          )}
          <CheckButton
            type="button"
            role="checkbox"
            aria-checked={checked}
            aria-label={t("card.markCompleted", {
              topic: task.topic || t("common.untitled"),
            })}
            title={checked ? t("card.markNotDone") : t("card.markDone")}
            $checked={checked}
            onClick={() => updateTask({ done: !checked })}
          >
            <Check fontSize="inherit" />
          </CheckButton>
          <IconButton
            aria-label={t("card.moreActions")}
            aria-haspopup="menu"
            aria-expanded={Boolean(menuAnchor)}
            className="card-menu-btn"
            size="small"
            onClick={(event) => setMenuAnchor(event.currentTarget)}
          >
            <MoreVert fontSize="small" />
          </IconButton>
        </HeaderRight>
      </CardHeader>
      <TopicRow>
        {TopicIcon && (
          <TopicIconWrap
            title={typeInfo ? t(`cardTypes.${task.type}`) : undefined}
            style={typeInfo ? { color: typeInfo.color } : undefined}
          >
            <TopicIcon fontSize="small" />
          </TopicIconWrap>
        )}
        <TopicInput
          $status={status}
          $checked={checked}
          type="text"
          aria-label={t("card.name")}
          placeholder={t("common.untitled")}
          value={task.topic}
          onChange={(event) => updateTask({ topic: event.target.value })}
        />
      </TopicRow>
      <TimeRange>
        {formatTime(taskStart)} – {formatTime(taskEnd)}
      </TimeRange>
      {(task.owner || task.note) && (
        <CardDetails
          type="button"
          aria-label={t("card.editDetails")}
          onClick={() => setDetailsOpen(true)}
        >
          {task.owner && <div className="card-owner">{task.owner}</div>}
          {task.note && <div className="card-note">{task.note}</div>}
        </CardDetails>
      )}
      {detailsOpen && (
        <CardDetailsDialog
          task={task}
          open={detailsOpen}
          onClose={() => setDetailsOpen(false)}
          onSave={(changes) => {
            setDetailsOpen(false);
            updateTask(changes);
          }}
        />
      )}
      <CardFooter>
        <TextField
          variant="standard"
          id={`time-${task.id}`}
          className="card-time-field"
          type="time"
          value={task.time}
          onChange={(event) => updateTask({ time: event.target.value })}
          slotProps={{
            htmlInput: {
              step: 1,
            },
          }}
        />
        <Menu
          anchorEl={menuAnchor}
          open={Boolean(menuAnchor)}
          onClose={() => setMenuAnchor(null)}
          anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
          transformOrigin={{ vertical: "top", horizontal: "right" }}
          slotProps={{ paper: { className: "card-menu" } }}
        >
          <MenuItem
            disabled={checked}
            onClick={() => {
              setMenuAnchor(null);
              completeTask();
            }}
          >
            <ListItemIcon>
              <TaskAlt fontSize="small" />
            </ListItemIcon>
            <ListItemText
              primary={t("card.complete")}
              secondary={
                status === "active"
                  ? t("card.completeActive")
                  : t("card.completeOther")
              }
            />
          </MenuItem>
          <MenuItem
            onClick={() => {
              setMenuAnchor(null);
              setDetailsOpen(true);
            }}
          >
            <ListItemIcon>
              <EditNote fontSize="small" />
            </ListItemIcon>
            <ListItemText
              primary={t("card.details")}
              secondary={t("card.detailsHint")}
            />
          </MenuItem>
          <MenuItem
            onClick={() => {
              setMenuAnchor(null);
              duplicateTask();
            }}
          >
            <ListItemIcon>
              <ContentCopy fontSize="small" />
            </ListItemIcon>
            <ListItemText
              primary={t("card.duplicate")}
              secondary={t("card.duplicateHint")}
            />
          </MenuItem>
          <Divider />
          <MenuItem
            className="card-menu-delete"
            onClick={() => {
              setMenuAnchor(null);
              saveAgenda(deleteArray);
            }}
          >
            <ListItemIcon>
              <Delete fontSize="small" />
            </ListItemIcon>
            <ListItemText
              primary={t("card.delete")}
              secondary={t("card.deleteHint")}
            />
          </MenuItem>
        </Menu>
      </CardFooter>
    </Card>
  );
}

export default function Agenda({ path, hidePast }) {
  const navigate = useNavigate();
  const json = CreateParamsJson();
  const [actualtime, setActualtime] = useState(moment());
  const { t } = useLanguage();
  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 4 } }),
    useSensor(KeyboardSensor, { coordinateGetter: sortableKeyboardCoordinates })
  );

  useEffect(() => {
    const interval = setInterval(() => setActualtime(moment()), 1000);
    return () => clearInterval(interval);
  }, []);

  if (json.agenda) {
    const agenda = JSON.parse(json.agenda);
    const starttime = moment(json.starttime);
    const statuses = computeAgendaStatuses(
      agenda,
      starttime,
      planClock(json, actualtime)
    );
    const visible = agenda
      .map((task, originalIndex) => ({
        task,
        originalIndex,
        status: statuses[originalIndex],
      }))
      .filter(
        (item) => !hidePast || (item.status !== "done" && !item.task.done)
      );

    if (visible.length === 0) {
      return <p className="agenda-empty">{t("board.allDone")}</p>;
    }

    return (
      <DndContext
        sensors={sensors}
        collisionDetection={closestCenter}
        onDragEnd={(event) =>
          onDragEnd(event, agenda, visible, json, navigate, path)
        }
      >
        <DragAndDropContainer>
          <DroppableContainer visible={visible} path={path} />
        </DragAndDropContainer>
      </DndContext>
    );
  }
  return null;
}
